/**
 * Menu and QR Code API service
 * Handles shop menu retrieval and QR code generation
 */

import { apiClient } from './client';
import { MenuResponse, QRCodeResponse, ShopMenuDTO } from '../types';
import { getConfig, createLogger } from '../config/environment';

const logger = createLogger('MenuService');
const config = getConfig();

export class MenuService {
  /**
   * Get shop menu with all catalog items
   * GET /api/shops/{shopId}/menus
   */
  async getShopMenu(shopId: string): Promise<ShopMenuDTO> {
    try {
      logger.info(`Fetching shop menu for: ${shopId}`);
      const response = await apiClient.get<ShopMenuDTO>(
        `${config.menusEndpoint}/${shopId}/menus`
      );

      if (response.status !== 200) {
        throw new Error('Failed to fetch shop menu');
      }

      return response.data;
    } catch (error) {
      logger.error('Failed to fetch shop menu', error);
      throw error;
    }
  }

  /**
   * Generate QR code for shop menu
   * POST /api/shops/{shopId}/qr-code/generate
   * Links to: https://myshop.soanch.com/web-menu?shopid={shopId}
   */
  async generateQRCode(shopId: string): Promise<QRCodeResponse> {
    try {
      logger.info(`Generating QR code for shop: ${shopId}`);
      const response = await apiClient.post<QRCodeResponse>(
        `${config.qrCodeEndpoint}/${shopId}/qr-code/generate`,
        {}
      );

      if (response.status !== 200) {
        throw new Error('Failed to generate QR code');
      }

      logger.info('QR code generated successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to generate QR code', error);
      throw error;
    }
  }

  /**
   * Get previously generated QR code
   * GET /api/shops/{shopId}/qr-code
   */
  async getQRCode(shopId: string): Promise<QRCodeResponse> {
    try {
      logger.info(`Fetching QR code for shop: ${shopId}`);
      const response = await apiClient.get<QRCodeResponse>(
        `${config.qrCodeEndpoint}/${shopId}/qr-code`
      );

      if (response.status !== 200) {
        throw new Error('Failed to fetch QR code');
      }

      return response.data;
    } catch (error) {
      logger.error('Failed to fetch QR code', error);
      throw error;
    }
  }
}

// Create singleton instance
export const menuService = new MenuService();

